import React, { useState } from 'react';
import { Button, Dropdown } from 'antd';
import {
  DownOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import './dashboardFleet.css';

const SETTINGS_PATH = '/customer/settings';

function cleanText(value) {
  if (value == null) return '';
  const text = String(value).trim();
  if (!text) return '';
  const lowered = text.toLowerCase();
  if (lowered === 'null' || lowered === 'undefined') return '';
  return text;
}

function normalized(value) {
  return cleanText(value).toLowerCase();
}

function normalizedAccountRole(value) {
  return cleanText(value).toUpperCase();
}

export function resolveDashboardAccountRoleLabel(session) {
  const broadRole = normalized(session?.role);
  if (broadRole === 'customer') {
    const accountRole = normalizedAccountRole(session?.customerAccountRole);
    if (accountRole === 'OWNER') return 'Account Owner';
    if (accountRole === 'MANAGER') return 'Fleet Manager';
    return 'Customer';
  }
  if (broadRole === 'technician') return 'Technician';
  if (broadRole === 'operator') return 'Operator';
  if (broadRole === 'admin' || broadRole === 'administrator' || broadRole === 'internal_admin') {
    return 'ManageLift Admin';
  }
  return '';
}

export function canShowDashboardBilling(session, customerName) {
  return (
    normalized(session?.role) === 'customer' &&
    normalizedAccountRole(session?.customerAccountRole) === 'OWNER' &&
    session?.canManageBilling === true &&
    Boolean(cleanText(customerName ?? session?.customerName ?? session?.customer_name))
  );
}

export function canShowCompanyDetails(session) {
  return (
    normalized(session?.role) === 'customer' &&
    normalizedAccountRole(session?.customerAccountRole) === 'OWNER'
  );
}

export function buildDashboardAccountMenuModel({ session, customerName } = {}) {
  const name = cleanText(session?.name);
  const email = cleanText(session?.email);
  const primaryIdentity = name || email;
  const secondaryEmail = email && email !== primaryIdentity ? email : '';
  const company = cleanText(customerName ?? session?.customerName ?? session?.customer_name);
  const roleLabel = resolveDashboardAccountRoleLabel(session);
  const showCompanyDetails = canShowCompanyDetails(session);
  const showBilling = canShowDashboardBilling(session, company);
  const showSettings = normalized(session?.role) === 'customer' && ['OWNER', 'MANAGER'].includes(normalizedAccountRole(session?.customerAccountRole));

  const identityLines = [
    primaryIdentity ? { key: 'primary', text: primaryIdentity, strong: true } : null,
    secondaryEmail ? { key: 'email', text: secondaryEmail } : null,
    company ? { key: 'company', text: company } : null,
    roleLabel ? { key: 'role', text: roleLabel } : null,
  ].filter(Boolean);

  const items = [
    ...(identityLines.length ? [{
      key: 'identity',
      disabled: true,
      label: (
        <div className="dashboard-account-menu-identity" aria-label="Account identity">
          {identityLines.map((line) => (
            <div
              key={line.key}
              className={`dashboard-account-menu-identity-line${line.strong ? ' is-primary' : ''}`}
            >
              {line.text}
            </div>
          ))}
        </div>
      ),
    }, { type: 'divider' }] : []),
    ...(showSettings ? [{ key: 'settings', icon: <SettingOutlined />, label: 'Settings' }] : []),
    ...(showSettings ? [{ type: 'divider' }] : []),
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout' },
  ];

  return { items, showCompanyDetails, showBilling, showSettings, identityLines, roleLabel };
}

export default function DashboardAccountMenu({
  className = '',
  customerName,
  label = 'Account',
  logout,
  menuItems,
  navigate,
  onOpenChange,
  open,
  overlayClassName = 'dashboard-fleet-account-dropdown',
  session,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof open === 'boolean';
  const accountMenuOpen = isControlled ? open : internalOpen;
  const model = buildDashboardAccountMenuModel({ session, customerName });
  const items = menuItems || model.items;

  const handleOpenChange = (nextOpen) => {
    if (!isControlled) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  return (
    <Dropdown
      menu={{
        items,
        onClick: ({ key }) => {
          if (key === 'logout') logout?.();
          if (key === 'settings') navigate?.(SETTINGS_PATH);
        },
      }}
      onOpenChange={handleOpenChange}
      open={accountMenuOpen}
      overlayClassName={overlayClassName}
      placement="bottomRight"
      trigger={['click']}
    >
      <Button
        className={`dashboard-fleet-account-menu${accountMenuOpen ? ' is-open' : ''}${className ? ` ${className}` : ''}`}
        icon={<UserOutlined />}
        shape="round"
      >
        <span className="dashboard-fleet-account-label">{label}</span>
        <DownOutlined />
      </Button>
    </Dropdown>
  );
}
